import React from 'react';
import { Input, Button, AutoComplete, Tag, Typography } from 'antd';
import PhotoStrip from '../Shared/PhotoStrip';
import CameraCaptureField from '../Shared/CameraCaptureField';
import { DVIInspectionItem, DVIItemStatus } from '../../types/dvi';
import { DviNotePresetGroup, DviNotePresetValue } from '../../services/api/dviNotePresetsApi';
import { resolvePresetGroup } from './notePresets';

const { Text } = Typography;

export interface DVIPointRowNoteFields {
  notes?: string | null;
  reading_value?: string | null;
  unit?: string | null;
  measurement_label?: string | null;
}

export interface DVIPointRowProps {
  item: DVIInspectionItem;
  onStatusChange: (status: Exclude<DVIItemStatus, 'pending'>, notes?: string | null) => void;
  onConvert: (item: DVIInspectionItem) => void;
  onPhotoUploaded?: () => void;
  onReadingChange?: (itemId: number, reading_value: string) => void;
  /**
   * WP-07 — persist the note / measurement WITHOUT requiring a status, so an
   * observation can be recorded on a photo-type point and before the point has
   * been judged.
   */
  onNoteChange?: (itemId: number, fields: DVIPointRowNoteFields) => void;
  /**
   * All of the shop's preset groups, fetched once by the form. Each row picks
   * its own group locally — see notePresets.ts for why this is not per-row I/O.
   */
  presetGroups?: DviNotePresetGroup[];
}

const STATUS_BUTTONS: Array<{
  value: Exclude<DVIItemStatus, 'pending'>;
  label: string;
  bg: string;
  fg: string;
}> = [
  { value: 'pass', label: 'Pass', bg: '#22c55e', fg: 'white' },
  { value: 'attention', label: 'Attention', bg: '#facc15', fg: '#222' },
  { value: 'fail', label: 'Fail', bg: '#ef4444', fg: 'white' },
];

export const DVIPointRow: React.FC<DVIPointRowProps> = ({
  item, onStatusChange, onConvert, onPhotoUploaded, onReadingChange, onNoteChange, presetGroups,
}) => {
  const [notesDraft, setNotesDraft] = React.useState(item.notes ?? '');
  const [reloadKey, setReloadKey] = React.useState(0);
  const [readingDraft, setReadingDraft] = React.useState(item.reading_value ?? '');
  const type = item.point_type ?? 'status';

  // Keep drafts in step when the parent refetches after a save, otherwise a
  // saved note is visually reverted by the next render.
  React.useEffect(() => { setNotesDraft(item.notes ?? ''); }, [item.notes]);
  React.useEffect(() => { setReadingDraft(item.reading_value ?? ''); }, [item.reading_value]);

  // The group that governs this point, and therefore the label the owner chose
  // for it: "Wear" on brakes, "Wear Amount" on tires, "Damage Location" on
  // exterior. Never a hardcoded string — that is the whole point of WP-07.
  const group = React.useMemo(
    () => resolvePresetGroup(presetGroups, item.point_name, item.category_name),
    [presetGroups, item.point_name, item.category_name],
  );
  const presetValues: DviNotePresetValue[] = group?.values ?? [];
  // A group with zero values is a legitimate state (an owner who made the group
  // but hasn't filled it, or retired every entry). Show the labelled field with
  // free text rather than hiding it — hiding would look like a broken save.
  const measurementLabel = group?.label ?? null;

  const commitNote = (next: string) => {
    if ((next || null) === (item.notes || null)) return;
    if (onNoteChange) {
      onNoteChange(item.id, { notes: next || null });
    } else if (item.status !== 'pending') {
      // Fallback for callers that predate onNoteChange: the status path is the
      // only way through, and it exists only for a judged point.
      onStatusChange(item.status as Exclude<DVIItemStatus, 'pending'>, next || null);
    }
  };

  /**
   * Applying a preset writes the value, its unit, and a SNAPSHOT of the label,
   * then — only when the owner configured a severity — drives the condition
   * indicator. The thresholds live in the preset rows, never in this file.
   */
  const applyPreset = (raw: string) => {
    setReadingDraft(raw);
    const match = presetValues.find((v) => v.value === raw);
    const fields: DVIPointRowNoteFields = {
      reading_value: raw || null,
      unit: match?.unit ?? item.unit ?? null,
      measurement_label: measurementLabel,
    };
    if (match?.severity) {
      // Status carries the note fields along in one request.
      onStatusChange(match.severity, notesDraft || null);
    }
    onNoteChange?.(item.id, fields);
    if (!onNoteChange && !match?.severity) {
      // Legacy caller: reading is the only channel available.
      onReadingChange?.(item.id, raw);
    }
  };

  // Requirement tag rendered next to the point name.
  const requirementTag = item.requirement === 'required' ? (
    <Tag color="gold">Required</Tag>
  ) : item.requirement === 'mandatory' ? (
    <Tag color="red">Mandatory</Tag>
  ) : null;

  const nameRow = (
    <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {item.point_name}
      {requirementTag}
      {!!item.is_custom && <Tag color="blue">Custom</Tag>}
    </div>
  );

  /**
   * The owner-labelled measurement field. Rendered for EVERY point type once a
   * group resolves, because "how worn is it" is not a property of whether the
   * point happens to be a reading-type point.
   *
   * AutoComplete, not Select: free text must stay possible when no preset fits.
   */
  const measurementBlock = group ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label
        htmlFor={`measurement-${item.id}`}
        style={{ fontSize: 12, color: '#666', fontWeight: 500 }}
      >
        {measurementLabel}
      </label>
      <AutoComplete
        id={`measurement-${item.id}`}
        data-testid={`measurement-input-${item.id}`}
        value={readingDraft}
        onChange={setReadingDraft}
        onSelect={applyPreset}
        onBlur={() => {
          if (readingDraft !== (item.reading_value ?? '')) applyPreset(readingDraft);
        }}
        options={presetValues.map((v) => ({
          value: v.value,
          label: v.unit ? `${v.value} ${v.unit}` : v.value,
        }))}
        placeholder={`Select or type ${(measurementLabel || '').toLowerCase()}`}
        allowClear
        style={{ width: '100%' }}
        filterOption={(input, option) =>
          String(option?.value ?? '').toLowerCase().includes(input.toLowerCase())
        }
      />
    </div>
  ) : null;

  /**
   * The note. Always present, on every point type, regardless of status — this
   * is the owner's core complaint: a caption was reachable only by uploading a
   * photo, so an observation with no photo could not be recorded at all.
   */
  const noteBlock = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Input.TextArea
        data-testid={`note-input-${item.id}`}
        aria-label="Inspection note"
        placeholder="Notes (for customer)"
        value={notesDraft}
        onChange={e => setNotesDraft(e.target.value)}
        onBlur={() => commitNote(notesDraft)}
        rows={2}
      />
      {/*
        A placeholder is not a warning: it disappears at the first keystroke, which
        is exactly when it matters. This note goes onto the customer's approval page
        word for word — a real one read "Front — Notes: Shit is wrecked" — and there
        is no internal/customer split on notes the way there is on photos. So the
        reminder stays on screen while it is being typed.
      */}
      {notesDraft.trim().length > 0 && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          The customer reads this exactly as written.
        </Text>
      )}
    </div>
  );

  // Photos. The item note travels onto the attachment as its caption too, so
  // the customer-facing photo caption keeps working — but the note no longer
  // DEPENDS on a photo existing, which is the behaviour change that matters.
  const photoBlock = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <PhotoStrip
        entityType="dvi_inspection_item"
        entityId={item.id}
        subType="dvi"
        editable
        reloadKey={reloadKey}
      />
      <CameraCaptureField
        purpose="dvi"
        entityType="dvi_inspection_item"
        entityId={item.id}
        notes={notesDraft || undefined}
        visibility="customer"
        onUploaded={() => { setReloadKey(k => k + 1); onPhotoUploaded?.(); }}
        buttonLabel="📷 Add Photo"
      />
    </div>
  );

  const wrapperStyle: React.CSSProperties = {
    padding: '10px 6px',
    borderBottom: '1px solid #eee',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  };

  if (type === 'photo') {
    return (
      <div style={wrapperStyle}>
        {nameRow}
        {measurementBlock}
        {noteBlock}
        {photoBlock}
      </div>
    );
  }

  if (type === 'reading') {
    // A reading point with a configured preset group uses the labelled preset
    // field; without one it keeps the plain numeric input it has always had.
    return (
      <div style={wrapperStyle}>
        {nameRow}
        {measurementBlock ?? (
          <Input
            data-testid={`reading-input-${item.id}`}
            value={readingDraft}
            onChange={e => setReadingDraft(e.target.value)}
            onBlur={() => {
              if (readingDraft !== (item.reading_value ?? '')) {
                onReadingChange?.(item.id, readingDraft);
              }
            }}
            addonAfter={item.unit || undefined}
            placeholder="Enter reading"
          />
        )}
        {noteBlock}
        {photoBlock}
      </div>
    );
  }

  // type === 'status' (default)
  const showConvert = item.status === 'attention' || item.status === 'fail';

  return (
    <div style={wrapperStyle}>
      {nameRow}

      <div style={{ display: 'flex', gap: 6 }}>
        {STATUS_BUTTONS.map(b => (
          <button
            key={b.value}
            aria-label={b.label}
            onClick={() => onStatusChange(b.value)}
            style={{
              flex: 1, minHeight: 44, minWidth: 44,
              borderRadius: 6,
              border: item.status === b.value ? '2px solid #222' : '1px solid #ccc',
              background: item.status === b.value ? b.bg : '#fafafa',
              color: item.status === b.value ? b.fg : '#666',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {b.label}
          </button>
        ))}
      </div>

      {measurementBlock}
      {noteBlock}
      {photoBlock}

      {showConvert && (
        item.line_item_id == null ? (
          <Button type="primary" onClick={() => onConvert(item)}>
            + Add to estimate
          </Button>
        ) : (
          <div style={{ color: '#888', fontSize: 12 }}>
            ✓ Added to {item.line_item_type === 'estimate' ? 'estimate' : 'work order'}
            {' '}(#{item.line_item_id})
          </div>
        )
      )}
    </div>
  );
};

export default DVIPointRow;
